<template>
    <form v-if="status.detail && !loading" @submit.prevent="userUUID ? requestUpdate() : requestCreate()">
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-account mr-1"></i>
                    Basic Information
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group :label="$t('label.image')" label-cols="2" label-align="left">
                    <ImageUploader :key="userUUID" :multiple="false" :showImageTop="true"
                        :buttonText="$t('label.uploadX', [$t('label.image')])" @images-changed="uploadImage"
                        @images-removed="removeImage" :initial-images="createForm.image ? [createForm.image] : []"
                        ref="coverUploader" />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.firstName')" label-cols="4" label-align="left">
                    <b-form-input v-model.trim="createForm.firstName"
                        :class="{ 'is-invalid': submitted && !$v.createForm.firstName.required }"
                        autocomplete="off"></b-form-input>
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.firstName.required" class="text-right">
                        {{ $t("message.requiredX", [$t("label.firstName")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.lastName')" label-cols="4" label-align="left">
                    <b-form-input v-model.trim="createForm.lastName"
                        :class="{ 'is-invalid': submitted && !$v.createForm.lastName.required }"
                        autocomplete="off"></b-form-input>
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.lastName.required" class="text-right">
                        {{ $t("message.requiredX", [$t("label.lastName")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.email')" label-cols="4" label-align="left">
                    <b-form-input v-model.trim="createForm.email"
                        :class="{ 'is-invalid': submitted && $v.createForm.email.$error }"
                        autocomplete="off"></b-form-input>
                    <div v-if="submitted && $v.createForm.email.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.email.required">{{ $t("message.requiredX", [$t("label.email")])
                            }}</span>
                        <span v-if="!$v.createForm.email.email">{{ $t("message.invalidX", [$t("label.email")]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.phone')"
                    label-cols="4"
                    label-align="left"
                >
                    <vue-tel-input
                        v-on:country-changed="(country) => phoneCountryChanged(country)"
                        v-model.trim="createForm.phone"
                        :defaultCountry="createForm.phoneIso || 'MY'"
                        :autoDefaultCountry="false"
                        :autoFormat="false"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12 mb-2">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-sitemap mr-1"></i>
                    Organization
                </h6>
            </div>
            <div class="col-md-12">
                <div class="table-responsive mb-3">
                    <table class="table table-sm border mb-0">
                        <thead class="thead-light">
                            <tr>
                                <th width="6%" class="text-center align-middle text-nowrap">#</th>
                                <th width="22%" class="align-middle">{{ $t('module.company') }}</th>
                                <th width="22%" class="align-middle">{{ $t('module.department') }}</th>
                                <th width="22%" class="align-middle">{{ $t('module.office') }}</th>
                                <th width="22%" class="align-middle">{{ $t('module.position') }}</th>
                                <th width="6%" class="text-center align-middle"><i class="mdi mdi-dots-horizontal"></i>
                                </th>
                            </tr>
                        </thead>
                        <draggable v-model="createForm.userCompanies" tag="tbody" handle=".draggable-item-handle"
                            :animation="300" easing="cubic-bezier(0.25, 1, 0.5, 1)"
                            :move="event => !!event.draggedContext.element.companyUUID && !!event.relatedContext.element?.companyUUID">
                            <tr v-for="(item, index) in createForm.userCompanies" :key="index"
                                class="fade-in position-relative"
                                :style="{ zIndex: createForm.userCompanies.length - index }">
                                <td class="text-center align-middle text-nowrap">
                                    <i :class="[
                                        'mdi mdi-menu-swap font-size-18 mr-1',
                                        item.companyUUID ? 'draggable-item-handle cursor-pointer text-muted' : 'text-muted',
                                    ]"></i>
                                    <span class="text-muted">{{ index + 1 }}</span>
                                </td>
                                <td class="text-nowrap">
                                    <multiselect v-model="item.companyUUID" track-by="value" label="text"
                                        select-label="" deselect-label="" :placeholder="$t('module.company')"
                                        :options="getSelectableCompanies(index)" :searchable="true" :allow-empty="false"
                                        class="cursor-pointer" @input="() => {
                                            item.departmentUUID = null;
                                            item.officeUUID = null;
                                        }" />
                                </td>
                                <td class="text-nowrap">
                                    <multiselect v-model="item.departmentUUID" track-by="value" label="text"
                                        select-label="" deselect-label="" :placeholder="$t('module.department')"
                                        :options="getDepartmentOptions(item)" :searchable="true" :allow-empty="false"
                                        class="cursor-pointer" :disabled="!item.companyUUID" />
                                </td>
                                <td class="text-nowrap">
                                    <multiselect v-model="item.officeUUID" track-by="value" label="text" select-label=""
                                        deselect-label="" :placeholder="$t('module.office')"
                                        :options="getOfficeOptions(item)" :searchable="true" :allow-empty="false"
                                        class="cursor-pointer" :disabled="!item.companyUUID" />
                                </td>
                                <td class="text-nowrap">
                                    <multiselect v-model="item.positionUUID" track-by="value" label="text"
                                        select-label="" deselect-label="" :placeholder="$t('module.position')"
                                        :options="positionDdl" :searchable="true" :allow-empty="false"
                                        class="cursor-pointer" :disabled="!item.companyUUID" />
                                </td>
                                <td class="text-center align-middle">
                                    <b-button type="button" variant="outline-danger" size="sm" class=""
                                        @click="createForm.userCompanies.splice(index, 1)">
                                        <i class="mdi mdi-close"></i>
                                    </b-button>
                                </td>
                            </tr>
                        </draggable>
                        <tfoot>
                            <tr>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td class="text-center align-middle">
                                    <b-button type="button" variant="info" size="sm"
                                        @click="createForm.userCompanies.push(defaultUserCompany())">
                                        <i class="mdi mdi-plus"></i>
                                    </b-button>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-lock-open-variant mr-1"></i>
                    Access
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.appAccess')" label-cols="4" label-align="left">
                    <b-form-checkbox v-model.trim="createForm.appAccess" class="mt-2" switch
                        :disabled="rolesLoading || permissionDefinitionsLoading" @change="onAppAccessChanged">
                    </b-form-checkbox>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.appPassword')" label-cols="4" label-align="left" class="fade-in">
                    <b-input-group>
                        <b-form-input v-model.trim="createForm.appPassword"
                            :type="showAppPassword ? 'text' : 'password'" :disabled="!createForm.appAccess"
                            :class="[createForm.appAccess ? 'bg-transparent fade-in' : 'bg-light']"
                            autocomplete="off"></b-form-input>
                        <b-input-group-append>
                            <b-input-group-text class="cursor-pointer" @click="showAppPassword = !showAppPassword">
                                <i :class="showAppPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'"></i>
                            </b-input-group-text>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.portalAccess')" label-cols="4" label-align="left">
                    <b-form-checkbox v-model.trim="createForm.portalAccess" class="mt-2" switch
                        @change="value => !value && (createForm.portalPassword = null)">
                    </b-form-checkbox>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.portalPassword')" label-cols="4" label-align="left" class="fade-in">
                    <b-input-group>
                        <b-form-input v-model.trim="createForm.portalPassword"
                            :type="showPortalPassword ? 'text' : 'password'" :disabled="!createForm.portalAccess"
                            :class="[createForm.portalAccess ? 'bg-transparent fade-in' : 'bg-light']"
                            autocomplete="off"></b-form-input>
                        <b-input-group-append>
                            <b-input-group-text class="cursor-pointer"
                                @click="showPortalPassword = !showPortalPassword">
                                <i :class="showPortalPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'"></i>
                            </b-input-group-text>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <b-form-group :label="$t('module.role')" label-cols="2" label-align="left">
                    <multiselect :value="createForm.role" :options="roleOptions" track-by="value" label="text"
                        :placeholder="$t('label.selectUserRole')" :loading="rolesLoading"
                        :disabled="rolesLoading || rolesError || permissionDefinitionsLoading || permissionDefinitionsError"
                        :allow-empty="false" @select="applyRole" />
                    <b-form-text>{{ $t('label.userRolePermissionsHint') }}</b-form-text>
                    <b-button v-if="createForm.role && createForm.role.permissions" type="button" size="sm"
                        variant="outline-info" class="mt-2"
                        :disabled="rolesLoading || rolesError || permissionDefinitionsLoading || permissionDefinitionsError"
                        @click="applyRole(createForm.role)">{{ $t('label.applyRoleDefaults') }}</b-button>
                    <b-alert v-if="rolesError" show variant="danger" class="mt-2">
                        {{ $t('label.rolesLoadError') }}
                        <b-button type="button" size="sm" variant="outline-danger" @click="loadRoles">
                            {{ $t('label.retryPermissionDefinitions') }}
                        </b-button>
                    </b-alert>
                </b-form-group>
            </div>
        </div>
        <b-alert v-if="normalAppRoleMissing" show variant="danger">
            {{ $t('label.normalAppRoleMissing') }}
            <b-button type="button" size="sm" variant="outline-danger" @click="loadRoles">
                {{ $t('label.retryPermissionDefinitions') }}
            </b-button>
        </b-alert>
        <hr v-if="createForm.portalAccess || createForm.appAccess" />
        <div v-if="createForm.portalAccess || createForm.appAccess" class="row fade-in">
            <div class="col-md-12 mb-2">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-shield-key mr-1"></i>
                    User Permissions
                </h6>
            </div>
            <div v-if="permissionDefinitionsLoading" class="col-12 text-center p-3">
                <b-spinner small variant="info" />
            </div>
            <div v-else-if="permissionDefinitionsError" class="col-12">
                <b-alert show variant="danger">
                    {{ $t('label.permissionDefinitionsLoadError') }}
                    <b-button type="button" size="sm" variant="outline-danger" @click="loadPermissionDefinitions">
                        {{ $t('label.retryPermissionDefinitions') }}
                    </b-button>
                </b-alert>
            </div>
            <div v-for="item in userPermissionModules" :key="item.key" class="col-md-6">
                <b-form-group :label="item.name" label-cols="4" label-align="left">
                    <multiselect v-model="createForm.permission[item.key]" :options="item.options" track-by="value"
                        label="text" :placeholder="'Select'" :select-label="'Select'" :selected-label="'Selected'"
                        :deselect-label="'Select'" :searchable="true" :allow-empty="false" class="cursor-pointer">
                        <template slot="option" slot-scope="{ option }">
                            <div>{{ option.text }}</div>
                            <small class="d-block text-muted permission-description">{{ option.description }}</small>
                        </template>
                    </multiselect>
                    <b-form-text v-if="createForm.permission[item.key]">
                        {{ createForm.permission[item.key].description }}
                    </b-form-text>
                </b-form-group>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button type="button" variant="danger" class="mr-2 col-2" :disabled="!status.save" @click="$emit('done')">{{ $t('label.cancel')
                }}</b-button>
            <b-button type="submit" variant="info" class="col-2"
                :disabled="!status.save || ((createForm.portalAccess || createForm.appAccess) && !permissionsReady)">
                <span v-if="status.save">
                    {{ userUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import draggable from "vuedraggable";
import { userService } from '@/services/api/user.service';
import { lookupService } from '@/services/api/lookup.service';
import { roleLookupModel } from '@/models/api/roleLookup.model';
import { userPermissionDefinitionsModel, userPermissionSelections, raiseUserPermissionSelections } from '@/models/api/userPermissionDefinitions.model';
import { VueTelInput } from 'vue-tel-input';
import ImageUploader from '@/components/upload-image';
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { userMethods, lookupMethods } from "@/state/api";
import { USER_MODULE } from "@/state/api/user.state";

export default {
    props: {
        userUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                firstName: { required },
                lastName: { required },
                email: { required, email },
            },
        };
    },
    components: { Multiselect, draggable, ImageUploader, VueTelInput },
    async created() {
        await this.loadPermissionDefinitions();
        await this.loadRoles();
        await this.lookupCompanyDepartmentOffice({});
        await this.lookupPosition({});
        await this.getUserData();

        this.loading = false;
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            userPermissionModules: [],
            roleOptions: [],
            rolesLoading: false,
            rolesError: false,
            appDefaultPending: false,
            normalAppRoleMissing: false,
            permissionDefinitionsLoading: false,
            permissionDefinitionsError: false,
            showAppPassword: false,
            showPortalPassword: false,
            user: {
                uuid: "",
                isActive: false,
            },
            loading: !!this.userUUID,
        };
    },
    methods: {
        ...userMethods,
        ...lookupMethods,
        async loadPermissionDefinitions() {
            this.permissionDefinitionsLoading = true;
            this.permissionDefinitionsError = false;
            try {
                const result = await userService.getPermissionDefinitions();
                this.userPermissionModules = userPermissionDefinitionsModel(result);
                const selected = Object.fromEntries(Object.entries(this.createForm.permission || {})
                    .filter(([, option]) => option != null)
                    .map(([key, option]) => [key, option.value]));
                const current = { ...(this.userUUID ? this.data?.permission || {} : {}), ...selected };
                this.createForm.permission = userPermissionSelections(this.userPermissionModules, current);
            } catch (error) {
                this.permissionDefinitionsError = true;
            } finally {
                this.permissionDefinitionsLoading = false;
                if (this.appDefaultPending) this.applyAppDefault();
            }
        },
        async loadRoles() {
            this.rolesLoading = true;
            this.rolesError = false;
            try {
                this.roleOptions = roleLookupModel(await lookupService.getRoles());
                // Loading the options must not replace an existing user's custom permissions.
                if (this.createForm.roleUUID) {
                    this.createForm.role = this.roleOptions.find(role => role.value === this.createForm.roleUUID)
                        || this.createForm.role;
                }
            } catch (error) {
                this.rolesError = true;
            } finally {
                this.rolesLoading = false;
                if (this.appDefaultPending) this.applyAppDefault();
            }
        },
        onAppAccessChanged(enabled) {
            this.createForm.appAccess = enabled;
            if (!enabled) {
                this.createForm.appPassword = null;
                this.appDefaultPending = false;
                this.normalAppRoleMissing = false;
                return;
            }
            this.appDefaultPending = true;
            this.applyAppDefault();
        },
        applyAppDefault() {
            if (!this.appDefaultPending || this.rolesLoading || this.rolesError ||
                this.permissionDefinitionsLoading || this.permissionDefinitionsError) return;
            const role = this.roleOptions.find(role => role.text === 'Normal App User');
            this.normalAppRoleMissing = !role;
            if (!role) return;
            this.createForm.role = role;
            this.createForm.roleUUID = role.value;
            this.createForm.permission = userPermissionSelections(this.userPermissionModules, role.permissions);
            this.appDefaultPending = false;
        },
        applyRole(role) {
            if (!role || this.appDefaultPending || this.permissionDefinitionsLoading || this.permissionDefinitionsError) return;
            this.createForm.role = role;
            this.createForm.roleUUID = role.value;
            this.createForm.permission = raiseUserPermissionSelections(
                this.userPermissionModules, this.createForm.permission, role.permissions,
            );
        },
        defaultForm() {
            return {
                uuid: "",
                role: null,
                roleUUID: null,
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                phoneCode: "",
                phoneIso: "",
                appAccess: false,
                appPassword: null,
                portalAccess: false,
                portalPassword: null,
                permission: userPermissionSelections(this.userPermissionModules || []),
                userCompanies: [],
            };
        },
        defaultUserCompany() {
            return {
                companyUUID: null,
                departmentUUID: null,
                officeUUID: null,
                positionUUID: null,
                sequence: null,
            };
        },
        async getUserData() {
            if (this.userUUID) {
                await this.getUser({ uuid: this.userUUID })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                ...this.data,
                                role: this.roleOptions.find(role => role.value === this.data.roleUUID)
                                    || (this.data.roleUUID ? {
                                        value: this.data.roleUUID,
                                        text: this.data.role?.name || this.data.roleUUID,
                                    } : null),
                                phone: this.data.phone?.toString() || '',
                                appAccess: this.data.appAccess == 1 ? true : false,
                                portalAccess: this.data.portalAccess == 1 ? true : false,
                                permission: userPermissionSelections(this.userPermissionModules, this.data.permission || {}),
                                userCompanies: (this.data.userCompanies ?? []).map(item => ({
                                    companyUUID: item.company ? {
                                        value: item.company.uuid,
                                        text: item.company.name,
                                    } : null,
                                    officeUUID: item.office ? {
                                        value: item.office.uuid,
                                        text: item.office.name,
                                    } : null,
                                    departmentUUID: item.department ? {
                                        value: item.department.uuid,
                                        text: item.department.name,
                                    } : null,
                                    positionUUID: item.position ? {
                                        value: item.position.uuid,
                                        text: item.position.name,
                                    } : null,
                                    sequence: item.sequence,
                                })),
                            };
                        }
                    });
            }
        },
        uploadImage(image) {
            this.createForm.image = image[0]?.file;
        },
        removeImage() {
            this.createForm.image = null;
        },
        requestCreate() {
            if ((this.createForm.portalAccess || this.createForm.appAccess) && !this.permissionsReady) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    roleUUID: this.createForm.role?.value || this.createForm.roleUUID,
                    appAccess: this.createForm.appAccess ? 1 : 0,
                    portalAccess: this.createForm.portalAccess ? 1 : 0,
                };
                if (this.createForm.portalAccess || this.createForm.appAccess) {
                    data.permission = Object.fromEntries(
                        Object.entries(this.createForm.permission).map(([key, permission]) => [key, permission.value])
                    );
                    if (this.createForm.portalAccess && this.createForm.userCompanies.length > 0) {
                        data.userCompanies = this.createForm.userCompanies.map((item, index) => ({
                            companyUUID: item.companyUUID?.value,
                            officeUUID: item.officeUUID?.value,
                            departmentUUID: item.departmentUUID?.value,
                            positionUUID: item.positionUUID?.value,
                            sequence: index + 1,
                        }))
                    } else {
                        delete data.userCompanies;
                    }
                } else {
                    delete data.permission;
                    delete data.userCompanies;
                }

                this.createUser({ data, filter: this.searchCriteria })
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            if (!this.status.save) return;
            if ((this.createForm.portalAccess || this.createForm.appAccess) && !this.permissionsReady) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    roleUUID: this.createForm.role?.value || this.createForm.roleUUID,
                    appAccess: this.createForm.appAccess ? 1 : 0,
                    portalAccess: this.createForm.portalAccess ? 1 : 0,
                };
                if (this.createForm.portalAccess || this.createForm.appAccess) {
                    data.permission = Object.fromEntries(
                        Object.entries(this.createForm.permission).map(([key, permission]) => [key, permission.value])
                    );
                    if (this.createForm.portalAccess && this.createForm.userCompanies.length > 0) {
                        data.userCompanies = this.createForm.userCompanies.map((item, index) => ({
                            companyUUID: item.companyUUID?.value,
                            officeUUID: item.officeUUID?.value,
                            departmentUUID: item.departmentUUID?.value,
                            positionUUID: item.positionUUID?.value,
                            sequence: index + 1,
                        }))
                    } else {
                        delete data.userCompanies;
                    }
                } else {
                    delete data.permission;
                    delete data.userCompanies;
                }

                this.updateUser({ data, filter: this.searchCriteria })
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("saved");
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
        phoneCountryChanged(country) {
            this.createForm.phoneCode = "+" + country.dialCode;
            this.createForm.phoneIso = country.iso2
        },
        getSelectableCompanies(index) {
            const selected = this.createForm.userCompanies
                .filter((item, i) => i !== index && item.companyUUID)
                .map(item => item.companyUUID.value);
            return this.companyDepartmentOfficeDdl.filter(item => !selected.includes(item.value));
        },
        getDepartmentOptions(item) {
            const company = this.companyDepartmentOfficeDdl.find(company => company.value == item.companyUUID?.value);
            return company?.departments || [];
        },
        getOfficeOptions(item) {
            const company = this.companyDepartmentOfficeDdl.find(company => company.value == item.companyUUID?.value);
            return company?.offices || [];
        },
    },
    computed: {
        permissionsReady() {
            return !this.appDefaultPending && !this.permissionDefinitionsLoading && !this.permissionDefinitionsError &&
                this.userPermissionModules.length > 0 && this.userPermissionModules.every(module =>
                    module.options.includes(this.createForm.permission[module.key])
                );
        },
        ...mapState(USER_MODULE, ["status"]),
        companyDepartmentOfficeDdl() {
            return this.$store.state.api.lookup.companyDepartmentOfficeDdl || [];
        },
        positionDdl() {
            return this.$store.state.api.lookup.positionDdl || [];
        },
        data() {
            return this.$store.state.api.user.details;
        },
    },
}
</script>

<style scoped>
.permission-description {
    white-space: normal;
}
</style>
